import { HttpParams } from "@angular/common/http";
import { Component, forwardRef, Inject, ViewChild } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MatButtonToggleChange } from "@angular/material/button-toggle";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import { firstValueFrom } from "rxjs";
import { ErrorMessages, Permissions } from "../../common/constants";
import { isEmpty } from "../../common/helper";
import { ActionDefinitionSparePartReference, ActionDiscardReason, ActionExecutionState, ActionIssueType, ActionState, ActionStatus, AlertDefinition, Customer, Location, Metric, ProductModelSparePartDefinitionReference, SparePartDefinition, StoreOrderItem, StoreOrderItemType, Thing, WorkOrderStatus } from "../../model";
import { WearStatus } from "../../model/wear-status";
import { ActionDiscardReasonService } from "../../service/action-discard-reason.service";
import { ActionIssueTypeService } from "../../service/action-issue-type.service";
import { AlertDefinitionService } from "../../service/alert-definition.service";
import { AuthenticationService } from "../../service/authentication.service";
import { CustomPropertyService } from "../../service/custom-property.service";
import { MetricService } from "../../service/metric.service";
import { ProductModelSparePartDefinitionReferenceService } from "../../service/product-model-spare-part-definition-reference.service";
import { StoreCartService } from "../../service/store-cart.service";
import { UserActionStatusService } from "../../service/user-action-status.service";
import { WearStatusService } from "../../service/wear-status.service";
import { MessageComponent } from "../../shared/component";
import { CustomPropertyFormComponent } from "../../shared/custom-property/custom-property-form.component";
import { LocalizationPipe } from "../../shared/pipe";
import { StatusTagPipeBody } from "../../shared/pipe/status-tag.pipe";
import { ErrorUtility } from "../../utility/error-utility";
import { SparePartInputComponent } from "./spare-part-input.component";

@Component({
    selector: 'action-info-dialog-v2',
    template: require('./action-info-dialog-v2.component.html'),
    styles: [require('./action-info-dialog-v2.component.css')]
})
export class ActionInfoDialogV2Component {

    @ViewChild('saveMessage') saveMessage: MessageComponent;

    @ViewChild('noteForm') noteForm: NgForm;

    @ViewChild('customPropForm') customPropForm: CustomPropertyFormComponent;

    @ViewChild(SparePartInputComponent) sparePartInput: SparePartInputComponent;

    wearMetric: Metric;
    actionStatus: ActionStatus;
    sparePartDefinitionMap: { [id: string]: { sparePartDefinition: SparePartDefinition, quantity: number } };
    showConfirmationForm: boolean;
    updating: boolean;
    error: string;
    actionDuration: string;
    locale: string;
    timezone: string;
    confirmReplacementEnabled: boolean;
    addToCartEnabled: boolean;
    loaded: boolean;
    showWearDetails: boolean;
    isHistorical: boolean;
    customer: Customer;
    location: Location;
    thing: Thing;
    showExceedStandardUsageMessage: boolean;
    statusTagPipeBody: StatusTagPipeBody = { icon: null, iconStyle: null, color: '#7C0505', backgroundColor: '#F80A0A26' };
    initialExecutionState: ActionExecutionState;
    selectedExecutionState: ActionExecutionState;
    insideWorkOrder: boolean;
    canUpdateState: boolean;
    issueTypes: ActionIssueType[];
    actionDiscardReasons: ActionDiscardReason[];
    mandatoryDiscardReason: boolean;
    detailsProperties: { label: string, value: string }[] = [];
    showCustomProperties: boolean;
    showSparePartInput: boolean;

    private saveBody: any;

    constructor(
        @Inject(forwardRef(() => StoreCartService)) private storeCartService: StoreCartService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => LocalizationPipe)) private localizationPipe: LocalizationPipe,
        @Inject(forwardRef(() => UserActionStatusService)) private userActionStatusService: UserActionStatusService,
        @Inject(forwardRef(() => AlertDefinitionService)) private alertDefinitionService: AlertDefinitionService,
        @Inject(forwardRef(() => WearStatusService)) private wearStatusService: WearStatusService,
        @Inject(forwardRef(() => ProductModelSparePartDefinitionReferenceService)) private productModelSparePartDefinitionReferenceService: ProductModelSparePartDefinitionReferenceService,
        @Inject(forwardRef(() => MatDialogRef)) public dialogRef: MatDialogRef<ActionInfoDialogV2Component>,
        @Inject(MAT_DIALOG_DATA) data,
        @Inject(forwardRef(() => MetricService)) private metricService: MetricService,
        @Inject(forwardRef(() => ActionIssueTypeService)) private actionIssueTypeService: ActionIssueTypeService,
        @Inject(forwardRef(() => ActionDiscardReasonService)) private actionDiscardReasonService: ActionDiscardReasonService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService
    ) {
        this.isHistorical = data.isHistorical;
        this.wearMetric = data.wearMetric;
        this.actionStatus = data.actionStatus;
        this.extractContextObjects();
        this.sparePartDefinitionMap = data.sparePartDefinitionMap;
        const user = this.authenticationService.getUser();
        this.locale = user.locale || user.language || 'en';
        this.timezone = user.timezone || 'UTC';
        this.actionDuration = this.getActionDuration();
        this.addToCartEnabled = this.storeCartService.canAddToCart() && !this.isHistorical;
        if (!this.wearMetric) {
            this.retrieveSparePartDefinition();
        } else {
            this.loaded = true;
        }
        this.initialExecutionState = this.isHistorical ? (this.actionStatus as any).state : (this.actionStatus.executionState || null);
        this.selectedExecutionState = this.initialExecutionState;
        this.insideWorkOrder = !!this.actionStatus.workOrderId;
        this.canUpdateState = !this.insideWorkOrder || (this.actionStatus.workOrder.status == WorkOrderStatus.EXECUTION && this.authenticationService.hasPermission(Permissions.WRITE_WORK_ORDER));
        this.showSparePartInput = this.authenticationService.getTenant().uncodedActionSparePartsEnabled && this.actionStatus.uncoded;
        this.loadActionIssueTypes();
        this.loadActionDiscardReasons();
        this.mandatoryDiscardReason = this.actionStatus.actionDefinition?.mandatoryDiscardReason;
        this.handleActionProperties();
    }

    private extractContextObjects(): void {
        this.thing = this.actionStatus.thing;
        this.thing.id = this.actionStatus.thingId;
        if (this.actionStatus.location && this.actionStatus.locationId) {
            this.location = this.actionStatus.location;
            this.location.id = this.actionStatus.locationId;
        }
        if (this.actionStatus.customer && this.actionStatus.customerId) {
            this.customer = this.actionStatus.customer;
            this.customer.id = this.actionStatus.customerId;
        }
    }

    private retrieveSparePartDefinition(): void {
        if (this.actionStatus?.actionDefinition?.alertDefinitionIds?.length) {
            let promises = [];
            this.actionStatus.actionDefinition.alertDefinitionIds.forEach(id => promises.push(this.alertDefinitionService.getAlertDefinitionById(id)));
            Promise.all(promises).then((alertDefinitions: AlertDefinition[]) => {
                const wearMetricId = alertDefinitions.find(ad => ad.activeConditionWearMetricId)?.activeConditionWearMetricId;
                if (wearMetricId) {
                    firstValueFrom(this.metricService.getMetricById(wearMetricId)).then(wearMetric => {
                        if (wearMetric) {
                            this.wearMetric = wearMetric;
                            this.showWearDetails = true;
                            this.getWearStatus(wearMetricId).then(wearStatus => {
                                if (wearStatus?.productModelSparePartDefinitionReferenceIds?.length) {
                                    this.getProductModelSparePartDefinitionReferences(wearStatus.productModelSparePartDefinitionReferenceIds).then(references => {
                                        this.buildSparePartDefinitionMap(references);
                                        this.loaded = true;
                                    }).catch(err => this.handleError(err));
                                } else {
                                    this.loaded = true;
                                }
                            });
                        }
                    }).catch(err => this.handleError(err));
                } else {
                    this.loaded = true;
                }
            }).catch(err => this.handleError(err));
        } else {
            this.loaded = true;
        }
    }

    private getWearStatus(wearMetricId: string): Promise<WearStatus> {
        if (this.isHistorical) {
            return Promise.resolve(this.actionStatus.wearStatus);
        } else {
            return this.wearStatusService.getWearStatusesFromThingIdAndMetricId(this.thing.id, wearMetricId).then(results => {
                return results?.length ? results[0] : null;
            });
        }
    }

    private handleError(err): void {
        this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR);
    }

    private getProductModelSparePartDefinitionReferences(referenceIds: string[]): Promise<any> {
        let params = new HttpParams();
        referenceIds.forEach(id => params = params.append('productModelSparePartDefinitionReferenceId', id));
        return this.productModelSparePartDefinitionReferenceService.getRecursivelyAllProductModelSparePartDefinitionReferences(null, null, params);
    }

    private buildSparePartDefinitionMap(references: ProductModelSparePartDefinitionReference[]): void {
        if (references?.length) {
            this.sparePartDefinitionMap = {};
            references?.forEach(ref => {
                const oldQuantity: number = this.sparePartDefinitionMap[ref.sparePartDefinitionId] ? this.sparePartDefinitionMap[ref.sparePartDefinitionId].quantity : 0;
                this.sparePartDefinitionMap[ref.sparePartDefinitionId] = { sparePartDefinition: ref.sparePartDefinition, quantity: (oldQuantity + ref.quantity) };
            });
        }
    }

    private getActionDuration(): string {
        let duration = moment.duration((this.isHistorical ? this.actionStatus.timestamp : new Date().getTime()) - this.actionStatus.startTimestamp);
        let numberOfDays = Math.round(duration.asDays());
        return (numberOfDays > 0 ? numberOfDays : '< 1') + " " + this.localizationPipe.transform("days");
    }

    private loadActionIssueTypes(): void {
        this.actionIssueTypeService.getAllIssueTypes().then(types => {
            this.issueTypes = types;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private loadActionDiscardReasons(): void {
        this.actionDiscardReasonService.getAllActionDiscardReasons().then(reasons => this.actionDiscardReasons = reasons)
            .catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.GET_DATA_ERROR));
    }

    private getSparePartDefinitionStoreItem(sparePartDefinitionId: string): StoreOrderItem {
        let newItem = new StoreOrderItem();
        newItem.itemId = sparePartDefinitionId;
        newItem.type = StoreOrderItemType.SPARE_PART;
        newItem.quantity = this.sparePartDefinitionMap[sparePartDefinitionId].quantity;
        newItem.thingId = this.thing.id;
        return newItem;
    }

    addToCart(): void {
        let newItems: StoreOrderItem[] = [];
        this.updating = true;
        Object.keys(this.sparePartDefinitionMap).forEach(key => {
            newItems.push(this.getSparePartDefinitionStoreItem(key));
        });
        this.storeCartService.addItemToCart(newItems).then(() => {
            this.updating = false;
            this.saveMessage.show();
            this.error = null;
        }).catch(err => this.error = ErrorUtility.getMessage(err, ErrorMessages.SAVE_DATA_ERROR));
    }


    back(): void {
        this.showConfirmationForm = false;
        this.saveBody = null;
    }

    confirmReplacement(): void {
        this.doSave();
    }

    isOverflow(el: HTMLElement): boolean {
        var isOverflowing = el.clientWidth < el.scrollWidth
            || el.clientHeight < el.scrollHeight;
        return isOverflowing;
    }

    close(): void {
        this.dialogRef.close();
    }


    updateShowExceedStandardUsageMessage(value: boolean): void {
        this.showExceedStandardUsageMessage = value;
    }

    toggleChangeExecutionState($event: MatButtonToggleChange) {
        const toggle = $event.source;
        if (toggle && $event.value.some(item => item == toggle.value)) {
            toggle.buttonToggleGroup.value = [toggle.value];
            this.selectedExecutionState = toggle.value;
        } else {
            this.selectedExecutionState = null;
        }
    }

    save(): void {
        this.saveBody = this.getSaveBody();
        if (this.selectedExecutionState?.startsWith('DONE') && this.wearMetric) {
            this.showConfirmationForm = true;
        } else {
            this.doSave();
        }
    }


    private doSave(): void {
        this.updating = true;
        this.userActionStatusService.patchActionStatus(this.actionStatus, this.saveBody).then(() => {
            this.updating = false;
            this.dialogRef.close(true);
        });
    }

    private getSaveBody(): any {
        const formValue = this.noteForm?.form.getRawValue();
        let body = Object.assign({}, formValue);
        if (this.insideWorkOrder) {
            body.executionState = this.selectedExecutionState;
        } else if (this.selectedExecutionState) {
            body.state = this.selectedExecutionState == ActionExecutionState.DISCARDED ? ActionState.DISCARDED : ActionState.DONE;
        }
        body.properties = this.customPropForm?.getProperties();
        if (this.sparePartInput) {
            let sparePartReferences: ActionDefinitionSparePartReference[] = [];
            const ref: ActionDefinitionSparePartReference = this.sparePartInput.getValue();
            if (ref.code) {
                sparePartReferences.push(ref);
            }
            body.sparePartReferences = sparePartReferences;
        }
        return body;
    }

    private handleActionProperties(): void {
        this.showCustomProperties = this.customPropertyService.getEditableActionPropertyDefinitions(this.actionStatus, null).length > 0 || (this.showSparePartInput);
        const readOnlyPropDef = this.customPropertyService.getReadOnlyActionPropertyDefinitions(this.actionStatus.uncoded, this.actionStatus.actionDefinition?.propertyReferences);
        let result: { label: string, value: string }[] = [];
        readOnlyPropDef.forEach(propDef => {
            result.push({ label: propDef.label || propDef.name, value: !this.actionStatus.properties || isEmpty(this.actionStatus.properties[propDef.name]) ? '-' : this.actionStatus.properties[propDef.name].toString() });
        });
        this.detailsProperties = result;
    };

    isCustomPropertiesFormValid(): boolean {
        if (!this.customPropForm) {
            return true;
        }
        return this.customPropForm.checkForm();
    }
}